import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Divider,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Alert,
} from '@mui/material';
import {
  Payment as PaymentIcon,
  Euro as EuroIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { fetchPaymentMethods } from '../../../app/store/slices/paymentMethodSlice';

/**
 * Dialog for recording a payment for a sale order
 * @param {boolean} open - Whether dialog is open
 * @param {function} onClose - Callback when dialog is closed
 * @param {function} onConfirm - Callback when payment is confirmed
 * @param {object} saleOrder - Sale order data
 */
const RecordPaymentDialog = ({ open, onClose, onConfirm, saleOrder }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [recording, setRecording] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState({});

  // Get payment methods from Redux store
  const paymentMethods = useSelector((state) => state.paymentMethod.paymentMethods) || [];
  const paymentMethodsLoading = useSelector((state) => state.paymentMethod.loading);
  const paymentMethodsLastFetched = useSelector((state) => state.paymentMethod.lastFetched);

  // Fetch payment methods when dialog opens if not already loaded
  useEffect(() => {
    if (open && !paymentMethodsLoading && !paymentMethodsLastFetched) {
      dispatch(fetchPaymentMethods());
    }
  }, [open, dispatch, paymentMethodsLoading, paymentMethodsLastFetched]);

  // Calculate due amount - calculate from line items if TotalIncl not available
  const calculateTotal = () => {
    if (saleOrder?.TotalIncl) {
      return saleOrder.TotalIncl;
    }
    // Calculate from line items if available
    if (saleOrder?.LineItems && Array.isArray(saleOrder.LineItems)) {
      return saleOrder.LineItems.reduce((sum, item) => sum + Number(item.TotalIncl || 0), 0);
    }
    return 0;
  };
  const dueAmount = calculateTotal() - (saleOrder?.PaidAmount || 0);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setPaymentAmount(dueAmount.toFixed(2));
      setPaymentMethod('');
      setPaymentDate(new Date().toISOString().split('T')[0]);
      setNotes('');
      setErrors({});
    }
  }, [open, dueAmount]);

  const validate = () => {
    const newErrors = {};
    
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      newErrors.paymentAmount = t('payment_amount_required');
    } else if (parseFloat(paymentAmount) > dueAmount) {
      newErrors.paymentAmount = t('payment_amount_exceeds_due');
    }
    
    if (!paymentMethod) {
      newErrors.paymentMethod = t('payment_method_required');
    }
    
    if (!paymentDate) {
      newErrors.paymentDate = t('payment_date_required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = async () => {
    if (!validate()) return;

    setRecording(true);
    try {
      const paymentData = {
        Amount: parseFloat(paymentAmount),
        PaymentMethodId: paymentMethod,
        PaymentDate: paymentDate,
        Notes: notes,
      };
      await onConfirm(paymentData);
      onClose();
    } catch (error) {

    } finally {
      setRecording(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={recording ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2.5,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          fontWeight: 700,
          fontSize: '1.1rem',
          color: '#4caf50',
          pb: 1.5,
        }}
      >
        <PaymentIcon />
        {t('record_payment')}
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>{t('due_amount')}:</strong> €{dueAmount.toFixed(2)}
          </Typography>
        </Alert>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField
            label={t('payment_amount')}
            type="number"
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(e.target.value)}
            error={!!errors.paymentAmount}
            helperText={errors.paymentAmount}
            fullWidth
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EuroIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            inputProps={{
              min: 0,
              step: 0.01,
            }}
          />

          <FormControl fullWidth required error={!!errors.paymentMethod}>
            <InputLabel>{t('payment_method')}</InputLabel>
            <Select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              label={t('payment_method')}
              disabled={paymentMethodsLoading}
            >
              {paymentMethodsLoading ? (
                <MenuItem disabled>
                  <em>{t('loading') || 'Loading...'}</em>
                </MenuItem>
              ) : paymentMethods.length === 0 ? (
                <MenuItem disabled>
                  <em>{t('no_payment_methods_available') || 'No payment methods available'}</em>
                </MenuItem>
              ) : (
                paymentMethods.map((method) => (
                  <MenuItem key={method.id} value={method.id}>
                    {method.name}
                  </MenuItem>
                ))
              )}
            </Select>
            {errors.paymentMethod && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {errors.paymentMethod}
              </Typography>
            )}
          </FormControl>

          <TextField
            label={t('payment_date')}
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            error={!!errors.paymentDate}
            helperText={errors.paymentDate}
            fullWidth
            required
            InputLabelProps={{
              shrink: true,
            }}
          />

          <TextField
            label={t('notes')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={3}
            fullWidth
            placeholder={t('add_payment_notes')}
          />
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1.5 }}>
        <Button
          onClick={onClose}
          color="inherit"
          disabled={recording}
          sx={{
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 1.5,
            px: 3,
          }}
        >
          {t('cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={recording}
          startIcon={recording ? <CircularProgress size={16} color="inherit" /> : <PaymentIcon />}
          sx={{
            fontWeight: 700,
            textTransform: 'none',
            borderRadius: 1.5,
            px: 3,
            bgcolor: '#4caf50',
            '&:hover': {
              bgcolor: '#388e3c',
            },
          }}
        >
          {recording ? t('recording') : t('record_payment')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecordPaymentDialog;
